import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../query';
import { coreApi } from '../api';
import { CauseSearch, CauseSearchIncludeOptions } from './causeSearch.types';
import { type ICause, IListCausesResponse, IShowCauseResponse } from './types';

/** @deprecated use useShowCauseW_Selector */
function useShowCause(uuid?: string, isMemberhub = false) {
  const queryClient = useQueryClient();
  const getCause = async () => {
    const memberhubQuery = isMemberhub ? '?memberhub=true' : '';
    const { data } = await coreApi.get<IShowCauseResponse>(`/causes/${uuid}${memberhubQuery}`);
    if (data.cause) {
      const shortener = data.cause.shortener;
      if (shortener && uuid == shortener) {
        queryClient.setQueryData(['cause', data.cause.uuid], data.cause);
      }
    }
    return data.cause;
  };
  return useQuery({
    queryKey: ['cause', uuid],
    queryFn: getCause,
    enabled: !!uuid,
    retry: false,
  });
}

export function useSearchCauses({
  queryOptions,
  pagination,
  enabled = true,
}: {
  queryOptions?: CauseSearch;
  pagination?: QueryPagination;
  enabled?: boolean;
}) {
  const { queryString, queryObject } = createQueryOptions<CauseSearch>({
    search: queryOptions,
    pagination,
  });

  const getCauses = async () => {
    const { data } = await coreApi.get<IListCausesResponse>(`/causes/search?${queryString}`);
    return data.causes;
  };

  return useQuery({
    queryKey: ['causes', 'search', queryObject],
    queryFn: getCauses,
    enabled: enabled,
  });
}

const showCauseOptions = ({
  uuid,
  include,
}: {
  uuid?: string;
  include?: CauseSearchIncludeOptions;
}) => {
  const { queryString, queryObject } = createQueryOptions<{
    options: { include?: CauseSearchIncludeOptions };
  }>({
    search: { options: { include: include } },
  });

  const getCauseFromCore = async () => {
    const { data } = await coreApi.get<IShowCauseResponse>(`/causes/${uuid}?${queryString}`);
    return data.cause;
  };

  return queryOptions({
    queryKey: ['cause', uuid, queryObject],
    queryFn: getCauseFromCore,
  });
};

// type ExtractOptions <TFunction> = Parameters<TFunction>[0] & Omit<ReturnType<TFunction>, 'queryKey' | 'queryFn' | 'select'>;
type UseShowCauseW_SelectorArgs<T = ICause> = {
  select?: (data: ICause) => T;
} & Omit<ReturnType<typeof showCauseOptions>, 'queryKey' | 'queryFn' | 'select'> &
  Parameters<typeof showCauseOptions>[0];

// TODO: Deprecate the above and replace with this one
const useShowCauseW_Selector = <T = ICause>({
  uuid,
  include,
  enabled = true,
  select,
  ...queryOptions
}: UseShowCauseW_SelectorArgs<T>) => {
  return useQuery({
    ...showCauseOptions({ uuid, include }),
    enabled: !!uuid && enabled,
    ...queryOptions,
    select,
  });
};

export { showCauseOptions, useShowCause, useShowCauseW_Selector };
